/**
 * Evidence workflow projection only. Canonical schemas, stored Intent, source
 * provenance, SED extraction and report authority retain their full contracts.
 * Unknown stage deliberately keeps the legacy schema/reference unchanged.
 */
import type { ChecklistItem } from '../../config/documentChecklists';
import type { AuthoredEvidenceFormState, AuthoredFieldDef } from '../../types/authoredEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type { ProjectIntent } from '../../types/siteRag';
import { SITE_PROJECT_INTENT_ID } from '../../config/reportSetupRequirements';
import { isScreeningIntentFieldKey } from '../stage2/stage2Catalogue';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';
import { isScreeningRecommendationMode } from '../intent/screeningIntentMode';
import type { ResolveReportFieldsResult } from './resolveReportFields';
import { coverageStatusFraction } from './resolveReportFields';
import { resolveReportSetupFields } from './resolveReportSetupFields';
import type { NextStep } from '../pathToGo/nextStep';
import { requirementIdForBareFocusId, requirementIdForSedField } from '../../config/requirementToFields';

export type EvidenceDecisionStage = 1 | 2;

// These inputs have real specialist consumers, but are not physical screening
// or engineering-validation questions. They remain in their canonical editors
// and frozen reports; an Evidence review never deletes or replaces them.
const SPECIALIST_INTENT_KEYS: ReadonlySet<string> = new Set([
  'annualDemandUtilizationRangePct.low', 'annualDemandUtilizationRangePct.high',
  'carbonCommitment',
  'offtake.structure', 'offtake.tenorYears', 'offtake.pricePerMwhUsd',
  'attestations.ppaSigned', 'attestations.speFormed', 'attestations.epcSigned',
  'attestations.slotReservationBinding',
]);

// The same requirement IDs keep provenance and downstream report consumers.
// Stage 1 asks for the resource facts, rather than the later formal deliverable.
const SCREENING_REQUIREMENTS: Record<string, { label: string; keys: readonly string[]; related: readonly string[] }> = {
  'parcel-survey': { label: 'Site area, access and land constraints', keys: ['parcelId', 'recordedAcreage', 'netUsableAcres', 'deedRestrictions', 'easements', 'notes'], related: ['parcel.acres', 'siteProfile.acres', 'siteProfile.slopePercent', 'parcel.landUse'] },
  zoning: { label: 'Zoning and permitted land use', keys: ['zoningCode', 'permittedUses', 'specialConditions', 'notes'], related: ['parcel.zoning', 'parcel.landUse', 'regulatory.state', 'regulatory.stateNotes'] },
  'flood-cert': { label: 'Flood and natural-hazard screening', keys: ['floodZone', 'notes'], related: ['flood.floodZone', 'flood.sfha', 'flood.floodRisk', 'flood.zoneOverlap', 'multiHazard.compositeRiskRating', 'multiHazard.hazards'] },
  'utility-letter': { label: 'Grid and gas access — available evidence', keys: ['utilityName', 'capacityOffered_MW', 'serviceDate', 'nearestTransmissionKm', 'substationDistKm', 'nearestPipelineKm', 'nearestPipelineOperator', 'nearestPipelineMassFlowValue', 'nearestPipelineMassFlowUnit', 'nearestPipelineMassFlowBasis', 'notes'], related: ['grid.utilityName', 'grid.capacity', 'grid.substationDistKm', 'grid.nearestTransmissionKm', 'onSiteInfrastructure.nearestPipelineKm', 'onSiteInfrastructure.nearestPipelineOperator', 'onSiteInfrastructure.nearestPipelineMassFlowValue', 'onSiteInfrastructure.nearestPipelineMassFlowUnit', 'onSiteInfrastructure.nearestPipelineGasCompositionSummary'] },
  'water-rights': { label: 'Water source, available volume and rights', keys: ['waterSource', 'permittedVolume_MGD', 'notes'], related: ['waterStress.stressLevel', 'waterStress.overallWaterRisk', 'waterResource.waterRightsStatus', 'waterResource.consumptiveUseAfy'] },
  'aquifer-study': { label: 'Groundwater availability', keys: ['aquiferName', 'estimatedYield_gpm', 'notes'], related: ['waterStress.stressLevel'] },
  'municipal-utility-letter': { label: 'Municipal water and wastewater availability', keys: ['supplyProvider', 'supplySourceDescription', 'supplyExcessMgd', 'siteWaterAllocationMgd', 'projectAverageWaterDemandMgd', 'projectPeakWaterDemandMgd', 'projectWaterMarginMgd', 'siteWastewaterAllocationMgd', 'projectPeakWastewaterDischargeMgd', 'projectWastewaterMarginMgd', 'minimumWaterPressurePsi', 'waterServiceDate', 'supplyNearestMainMiles', 'supplyLineToSite', 'wastewaterProvider', 'wastewaterExcessMgd', 'wastewaterLineToSite', 'appliesToProject', 'notes'], related: ['waterResource.municipalSupply', 'waterResource.wastewaterService', 'waterStress.stressLevel'] },
  'phase1-esa': { label: 'Known environmental constraints', keys: ['recRecognized', 'recDetails', 'notes'], related: ['environmental.hasWetlands', 'environmental.wetlandAcres', 'environmental.species', 'environmental.hasCriticalHabitat'] },
  wetland: { label: 'Wetlands screening', keys: ['wetlandsPresent', 'wetlandAcres', 'notes'], related: ['environmental.hasWetlands', 'environmental.wetlandAcres'] },
  'species-survey': { label: 'Protected species and habitat screening', keys: ['speciesObserved', 'criticalHabitat', 'notes'], related: ['environmental.species', 'environmental.hasCriticalHabitat'] },
  cultural: { label: 'Known cultural-resource constraints', keys: ['historicProperties', 'notes'], related: ['culturalResources.publicRegistryScreen', 'culturalResources.warnings'] },
  'fiber-survey': { label: 'Fiber and network access', keys: ['carrier', 'serviceType', 'capacity_Gbps', 'buildDistance_ft', 'notes'], related: ['connectivity'] },
  'community-feedback-summary': { label: 'Community and stakeholder context', keys: ['overallStance', 'keyThemes', 'notes'], related: ['publicSentiment'] },
};

/** Same schema for visible fields, denominator, ranking and authoring. */
export function evidenceSchemaForStage(
  requirementId: string,
  schema: readonly AuthoredFieldDef[],
  stage?: EvidenceDecisionStage,
): readonly AuthoredFieldDef[] {
  if (stage == null) return schema;
  if (requirementId === SITE_PROJECT_INTENT_ID) {
    const visible = schema.filter(field => field.key === 'notes' || (stage === 1
      ? isScreeningIntentFieldKey(field.key)
      : !SPECIALIST_INTENT_KEYS.has(field.key)));
    // Missing owner demand is not a prerequisite for a resource-bounded screen.
    return stage === 1 ? visible.map(field => field.required ? { ...field, required: false } : field) : visible;
  }
  if (stage === 1 && SCREENING_REQUIREMENTS[requirementId]) {
    const keys = new Set(SCREENING_REQUIREMENTS[requirementId]!.keys);
    return schema.filter(field => keys.has(field.key)).map(field => field.required ? { ...field, required: false } : field);
  }
  // The feasibility setup duplicates design/firm/carbon facts from Intent.
  // Screening can compare a supply architecture without an adopted design.
  if (requirementId === 'feasibility-report-setup' && stage === 1) {
    return schema.filter(field => field.key === 'btmStrategy' || field.key === 'notes');
  }
  if (requirementId === 'feasibility-report-setup' && stage === 2) {
    return schema.filter(field => field.key !== 'carbonCommitment');
  }
  return schema;
}

/** Preserve already stored hidden fields; never seed unseen values from a model. */
export function preserveHiddenEvidenceFields(
  form: AuthoredEvidenceFormState,
  existing: ExtractedClaim | undefined,
  fullSchema: readonly AuthoredFieldDef[],
  visibleSchema: readonly AuthoredFieldDef[],
): AuthoredEvidenceFormState {
  if (!existing?.structuredFields) return form;
  const visible = new Set(visibleSchema.map(field => field.key));
  const hidden: Record<string, string | number | boolean> = {};
  for (const field of fullSchema) {
    if (field.key === 'notes' || visible.has(field.key)) continue;
    const value = existing.structuredFields[field.key];
    if (value !== undefined) hidden[field.key] = value;
  }
  return Object.keys(hidden).length ? { ...form, fields: { ...hidden, ...form.fields } } : form;
}

/** Specialist report presentation controls do not belong to either Evidence stage. */
export function evidenceRequirementForStage(
  item: ChecklistItem,
  stage?: EvidenceDecisionStage,
  intent?: ProjectIntent | null,
): ChecklistItem | null {
  if (stage == null) return item;
  if (stage === 1 && item.kind === 'site-intent' && isScreeningRecommendationMode(intent)) return null;
  // Wizard configuration belongs in its report editor, not a source checklist.
  if (item.kind === 'report-setup') return null;
  if (stage === 1 && item.kind !== 'site-intent' && !SCREENING_REQUIREMENTS[item.id]) return null;
  // These professional design/title/procurement deliverables validate the
  // screen later. Public parcel/hazard/resource context stays visible now.
  if (stage === 1 && [
    'geotech', 'alta-survey', 'mineral-title', 'archaeological-survey',
    'btm-vendor-quote', 'vendor-quote-pack',
  ].includes(item.id)) return null;
  const context = stage === 1 ? SCREENING_REQUIREMENTS[item.id] : undefined;
  const projected = context ? { ...item, label: context.label,
    description: 'Review available site-specific or public screening evidence. Unknowns remain disclosed; formal engineering evidence is reviewed in Stage 2.' } : item;
  const schema = item.authoredSchema;
  if (!schema) return projected;
  const visible = evidenceSchemaForStage(item.id, schema, stage);
  if (!context && visible.length === schema.length && visible.every((field, index) => field === schema[index])) return item;
  return { ...projected, authoredSchema: visible };
}

/** Exact unknown choices are unresolved; No, false and zero remain substantive. */
function unresolvedStageField<T extends { value: unknown; displayValue: string; isEmpty: boolean }>(field: T): T {
  if (typeof field.value !== 'string') return field;
  const sentinel = field.value.trim().toLowerCase();
  if (sentinel !== 'unknown' && sentinel !== 'undecided') return field;
  const displayValue = sentinel === 'unknown' ? 'Unknown' : 'Undecided';
  return field.isEmpty && field.displayValue === displayValue ? field : { ...field, displayValue, isEmpty: true };
}

/** Keep engineering fields out of Stage 1, and unresolved choices out of both known counts. */
export function evidenceResolvedFieldsForStage(
  resolved: ResolveReportFieldsResult,
  requirementId: string,
  stage?: EvidenceDecisionStage,
): ResolveReportFieldsResult {
  if (stage == null) return resolved;
  const context = stage === 1 ? SCREENING_REQUIREMENTS[requirementId] : undefined;
  const keys = context ? new Set(context.keys) : undefined;
  const primary = (context ? resolved.primary.filter(field => keys!.has(field.key)) : resolved.primary)
    .map(unresolvedStageField);
  const related = (context ? resolved.related.filter(field => context.related.some(path => field.path === path || field.path.startsWith(path + '.'))) : resolved.related)
    // These parent objects repeat the municipal primary facts; their nested
    // leaves remain available in source details, never a second Known answer.
    .filter(field => requirementId !== 'municipal-utility-letter'
      || (field.path !== 'waterResource.municipalSupply' && field.path !== 'waterResource.wastewaterService'))
    .map(unresolvedStageField);
  const fraction = coverageStatusFraction({ primary, related });
  const structuredFilled = primary.filter(field => field.key !== 'notes' && !field.isEmpty).length;
  const structuredTotal = primary.filter(field => field.key !== 'notes').length;
  if (primary.length === resolved.primary.length && primary.every((field, index) => field === resolved.primary[index])
    && related.length === resolved.related.length && related.every((field, index) => field === resolved.related[index])
    && structuredFilled === resolved.structuredFilled && structuredTotal === resolved.structuredTotal
    && fraction.filled === resolved.statusFilled && fraction.total === resolved.statusTotal) return resolved;
  return { ...resolved, primary, related, structuredFilled, structuredTotal,
    statusFilled: fraction.filled, statusTotal: fraction.total };
}

/** Stage-scoped worklist; keeps the producer's ranking and decision authority. */
export function evidenceStepsForStage(
  steps: readonly NextStep[],
  stage: EvidenceDecisionStage | undefined,
  requirements: readonly ChecklistItem[],
  intent?: ProjectIntent | null,
): readonly NextStep[] {
  if (stage == null) return steps;
  const byId = new Map(requirements.map(item => [item.id, item]));
  const screeningSections = new Set(['parcel', 'siteProfile', 'siteResearch', 'grid', 'electricity',
    'onSiteInfrastructure', 'climate', 'waterStress', 'waterResource', 'flood', 'multiHazard', 'seismic',
    'geotechnical', 'topography', 'environmental', 'regulatory', 'connectivity', 'culturalResources', 'publicSentiment']);
  return steps.filter(step => {
    const target = step.resolver?.targetId ?? step.resolver?.sedPath
      ?? (step.id.startsWith('intent:') ? step.id.slice('intent:'.length) : undefined);
    if (step.resolver?.action === 'open-intent') {
      if (stage === 1 && isScreeningRecommendationMode(intent)) return false;
      if (!target || target === SITE_PROJECT_INTENT_ID) return false;
      const key = target.replace(/^projectIntent\./, '');
      const parent = key.split('.')[0]!;
      if (isIntentFieldExplicitlyUnknown(intent, key) || isIntentFieldExplicitlyUnknown(intent, parent)) return false;
      return stage === 1 ? isScreeningIntentFieldKey(key) : !SPECIALIST_INTENT_KEYS.has(key);
    }
    const directId = step.id.startsWith('coverage:') ? step.id.slice('coverage:'.length) : target;
    const direct = directId ? byId.get(directId) : undefined;
    if (direct) {
      const scoped = evidenceRequirementForStage(direct, stage, intent);
      if (!scoped) return false;
      if (scoped.kind === 'site-intent') {
        const fields = evidenceFieldsWithRecordedUnknowns(resolveReportSetupFields({ requirement: scoped, projectIntent: intent ?? undefined }), scoped.id, intent, stage);
        return (fields.reviewedFieldCount ?? 0) < fields.primary.length;
      }
      return true;
    }
    const section = target?.split('.')[0];
    // Provider gaps describe desktop resources, not professional certificates.
    if (stage === 1 && section && screeningSections.has(section)) return true;
    const bridgedId = target && (target.includes('.')
      ? requirementIdForSedField(section!, target.slice(section!.length + 1))
      : requirementIdForBareFocusId(target));
    const bridged = bridgedId ? byId.get(bridgedId) : undefined;
    if (bridged) return evidenceRequirementForStage(bridged, stage, intent) != null;
    // Never silently remove an unclassified authoritative final-decision issue.
    return stage === 2;
  });
}

/**
 * Recorded unknowns are reviewed answers, never invented physical values.
 * This display-only result cannot be supplied to extraction or calculations.
 */
export type ReviewedEvidenceFields = ResolveReportFieldsResult & {
  /** Answer review is distinct from substantive known-field completeness. */
  reviewedFieldCount?: number;
  recordedUnknownKeys?: readonly string[];
  unresolvedFieldKeys?: readonly string[];
};

export function evidenceFieldsWithRecordedUnknowns(
  resolved: ResolveReportFieldsResult,
  requirementId: string,
  intent: ProjectIntent | null | undefined,
  stage?: EvidenceDecisionStage,
): ReviewedEvidenceFields {
  if (stage == null || requirementId !== SITE_PROJECT_INTENT_ID) return resolved;
  const unknownKeys: string[] = [];
  const unresolvedKeys: string[] = [];
  const exactDemand = typeof intent?.powerRequirementMW === 'number'
    && Number.isFinite(intent.powerRequirementMW) && intent.powerRequirementMW > 0
    && !isIntentFieldExplicitlyUnknown(intent, 'powerRequirementMW')
    && !isIntentFieldExplicitlyUnknown(intent, 'powerRequirement');
  const rangeDemand = !exactDemand && intent?.powerRequirement != null && intent.powerRequirement !== 'undecided'
    && !isIntentFieldExplicitlyUnknown(intent, 'powerRequirement');
  // Exact demand and its derived range describe one input. Keep the original
  // schema/data for canonical editors and consumers; project one answer here.
  const visible = resolved.primary.filter(field => {
    if (field.key === 'powerRequirement') return rangeDemand;
    if (field.key === 'powerRequirementMW') return !rangeDemand;
    if (!isIntentFieldExplicitlyUnknown(intent, 'fuelAccess') && intent?.fuelAccess?.supply === 'none'
      && (field.key === 'fuelAccess.distanceKm' || (field.key === 'attestations.gasSupplyFirm' && field.isEmpty))) return false;
    if (!isIntentFieldExplicitlyUnknown(intent, 'capacityCreditIntent') && intent?.capacityCreditIntent?.target === 'none'
      && field.key === 'capacityCreditIntent.targetMW') return false;
    if (!isIntentFieldExplicitlyUnknown(intent, 'interconnectionStatus') && intent?.interconnectionStatus?.queueStatus === 'none'
      && (field.key === 'interconnectionStatus.queueId' || field.key === 'interconnectionStatus.studyPhase')) return false;
    return true;
  });
  const negativeChoices: Record<string, { value: unknown; label: string }> = {
    'fuelAccess.supply': { value: intent?.fuelAccess?.supply, label: 'No gas access' },
    'waterSource.rightsStatus': { value: intent?.waterSource?.rightsStatus, label: 'No water rights recorded' },
    'capacityCreditIntent.target': { value: intent?.capacityCreditIntent?.target, label: 'No capacity accreditation requested' },
    'interconnectionStatus.queueStatus': { value: intent?.interconnectionStatus?.queueStatus, label: 'No utility request yet' },
  };
  const primary = visible.map(field => {
    const parent = field.key.includes('.') ? field.key.split('.')[0]! : field.key;
    // The maintained reset/form records nested choices at their parent path.
    // A specific child marker is also supported for existing saved Intent.
    const recorded = [field.key, parent, ...(field.key === 'powerRequirementMW' ? ['powerRequirement'] : [])]
      .some(key => isIntentFieldExplicitlyUnknown(intent, key));
    const sentinel = typeof field.value === 'string' && /^(?:unknown|undecided)$/i.test(field.value.trim());
    if (!recorded && !sentinel) {
      const negative = negativeChoices[field.key];
      // A declared absence is a reviewed answer, not permission/capacity. The
      // legacy canonical resolver intentionally leaves these choices empty.
      if (negative?.value === 'none') return { ...field, value: 'none', displayValue: negative.label,
        isEmpty: false, sourceLabel: 'Site Project Intent' };
      return field;
    }
    unresolvedKeys.push(field.key);
    if (recorded) unknownKeys.push(field.key);
    return { ...field, displayValue: recorded ? 'Unknown (recorded)' : 'Undecided', isEmpty: true,
      ...(recorded ? { sourceLabel: 'Project Intent — recorded unknown' } : {}) };
  });
  // Unknown can only reduce substantive completeness, never raise it. Preserve
  // canonical values/provenance and track the separate reviewed count.
  const structuredFilled = primary.filter(field => field.key !== 'notes' && !field.isEmpty).length;
  return { ...resolved, primary, recordedUnknownKeys: unknownKeys, unresolvedFieldKeys: unresolvedKeys,
    structuredTotal: primary.filter(field => field.key !== 'notes').length,
    structuredFilled, statusFilled: structuredFilled + resolved.related.filter(field => !field.isEmpty).length,
    statusTotal: primary.filter(field => field.key !== 'notes').length + resolved.related.length,
    reviewedFieldCount: primary.filter(field => !field.isEmpty || unknownKeys.includes(field.key)).length };
}
